import React, { useContext } from 'react'
import styled from 'styled-components'
import { Flex, Hide, Text, Link } from 'pcln-design-system'
import analytics from '../../utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '../../constants/analytics'
import { Listing, CarListing, RudderProductDesc } from './types'
import BootstrapDataContext from '../../context/BootstrapDataContext'
import CarProduct from './CarProduct'
import HotelProduct from './HotelProduct'
import { InventoryWrapper } from './Common'

const ViewMoreDeals = styled(Link)`
  font-size: 12px;
  font-weight: bold;
`

const ScrollContainer = styled(Flex)`
  overflow: auto hidden;
  & > :last-child {
    margin-right: 0px;
  }
`

function fireEventForViewMoreDeals() {
  analytics.fireEvent({
    action: 'Feature Box CTA',
    category: ANALYTICS_CATEGORY_HOME,
    label: 'Cross Sell View More Deals'
  })
}

function isCarListing(listing: Listing): listing is CarListing {
  return (listing as CarListing).carExample !== undefined
}

function renderXSellProduct(
  listing: Listing,
  index: number,
  offerNum: string,
  offerToken: string,
  rudderParentProductCode: RudderProductDesc
) {
  if (isCarListing(listing)) {
    return (
      <CarProduct
        {...listing}
        key={listing.id}
        offerNum={offerNum}
        position={index + 1}
        rudderParentProductCode={rudderParentProductCode}
      />
    )
  }
  return (
    <HotelProduct
      {...listing}
      key={listing.hotelId}
      offerNum={offerNum}
      position={index + 1}
      offerToken={offerToken}
      rudderParentProductCode={rudderParentProductCode}
    />
  )
}

function renderXSellProducts(
  listings: Listing[],
  numProducts = 2,
  offerNum: string,
  offerToken: string,
  rudderParentProductCode: RudderProductDesc
) {
  return listings
    .slice(0, numProducts)
    .map((listing, index) =>
      renderXSellProduct(
        listing,
        index,
        offerNum,
        offerToken,
        rudderParentProductCode
      )
    )
}

export default function Inventory({
  xSellMessaging,
  xSellProduct,
  rudderChildProductCode,
  rudderParentProductCode,
  xSellUrl,
  listings,
  offerNum,
  offerToken
}: {
  xSellMessaging: string
  xSellProduct: 'rental car' | 'flight' | 'hotel'
  rudderChildProductCode: RudderProductDesc
  rudderParentProductCode: RudderProductDesc
  xSellUrl: string
  listings: Listing[]
  offerNum: string
  offerToken: string
}) {
  const {
    isMobile = false,
    webstats,
    customer
  } = useContext(BootstrapDataContext)
  const viewMoreDeals = ({ target }: { target?: string } = {}) => (
    <ViewMoreDeals
      data-cguid={webstats.clientGUID}
      data-email-address={customer.emailAddress}
      data-parent-offer={offerNum}
      data-label={`View more ${xSellProduct} deals`}
      data-child-product={rudderChildProductCode}
      data-parent-product={rudderParentProductCode}
      data-has-inventory={listings.length}
      data-source="homepage"
      data-sub-type="cross sell"
      data-rudder-event
      data-view-more
      onClick={fireEventForViewMoreDeals}
      href={xSellUrl}
      target={target}
    >
      View more {xSellProduct} deals
    </ViewMoreDeals>
  )
  const renderedProducts = renderXSellProducts(
    listings,
    isMobile ? 2 : 3,
    offerNum,
    offerToken,
    rudderParentProductCode
  )
  return (
    <InventoryWrapper>
      <Flex flexDirection="column">
        <Flex justifyContent="space-between" alignItems="baseline" mb={2}>
          <Text fontSize={[null, 1, null, null, 2]} fontWeight="bold">
            {xSellMessaging}
          </Text>
          <Hide xs sm md lg>
            {viewMoreDeals({ target: '_blank' })}
          </Hide>
        </Flex>
        <ScrollContainer>{renderedProducts}</ScrollContainer>
        <Hide xl xxl mt="10px">
          {viewMoreDeals()}
        </Hide>
      </Flex>
    </InventoryWrapper>
  )
}
